import { BATCH_ACTIONS } from 'pages/Batch/configs';
import type { ICropTemplateValue, IWatermarkAttrs } from 'pages/Batch/types';
import type { ICropAttrsBatch, IBatchData } from 'types/store';

import loadingImage from 'utils/images/loadingImage';
import watermarkGenerator from 'pages/Batch/utils/watermark/watermarkGenerator';

export interface ISerializeContext {
  cropTemplate: ICropTemplateValue;
  watermarkGenerator: typeof watermarkGenerator;
}

export function serializeStepParams(action: BATCH_ACTIONS, attrs: Record<string, any>, context: ISerializeContext): Record<string, unknown> | null {
  switch (action) {
    case BATCH_ACTIONS.removeBackground:
    case BATCH_ACTIONS.aiEnhance:
      return {};

    case BATCH_ACTIONS.wfx:
      return {
        effect: attrs.effect ?? null,
        editorLibCategoryId: attrs.editorLibCategoryId ?? null,
        params: attrs.params ?? {},
        overlayResourceUrl: attrs.overlayResourceUrl ?? null,
        overlayBlendMode: attrs.overlayBlendMode ?? 'normal',
        overlayIntensity: attrs.overlayIntensity ?? 100,
      };

    case BATCH_ACTIONS.adjust:
      return {
        params: attrs.params ?? {},
      };

    case BATCH_ACTIONS.applyBackground:
      return {
        textureUrl: attrs.texture?.src ?? null,
        color: attrs.color ?? null,
        objectPosition: attrs.objectPosition ?? null,
        ratio: attrs.ratio ?? null,
      };

    case BATCH_ACTIONS.resize:
      return {
        width: attrs.width,
        height: attrs.height,
        usePadding: attrs.usePadding,
        bgColor: attrs.bgColor ?? null,
      };

    case BATCH_ACTIONS.applyCrop: {
      const { sizes } = context.cropTemplate;
      if (!sizes || (!sizes.width && !sizes.height)) {
        return null;
      }
      return {
        templateTitle: context.cropTemplate.title,
        templateSizes: sizes,
        rotate: attrs.rotate ?? 0,
        flip: attrs.flip ?? { x: 1, y: 1 },
      };
    }

    case BATCH_ACTIONS.watermark: {
      const gen = context.watermarkGenerator;
      return {
        logoUrl: gen.logo?.src ?? null,
        position: gen.attrs.position,
        opacity: gen.attrs.opacity,
        size: gen.attrs.size,
        rotateAngle: gen.attrs.rotateAngle,
      };
    }

    case BATCH_ACTIONS.generateAIBackground:
      return { prompt: attrs.prompt ?? null };

    // Non-replayable: depends on session-specific enhanceMapping URLs that expire
    case BATCH_ACTIONS.applyEnhance:
      return null;

    case BATCH_ACTIONS.applyWfx:
      return null;

    case BATCH_ACTIONS.applyAdjust:
      return null;

    case BATCH_ACTIONS.composedEnhance:
      return {
        enhanceEffectId: attrs.enhanceEffectId ?? null,
        enhanceIntensity: attrs.enhanceIntensity ?? 0,
        blurEffectId: attrs.blurEffectId ?? null,
        blurIntensity: attrs.blurIntensity ?? 0,
      };

    default: {
      const exhaustiveCheck: never = action;
      return exhaustiveCheck;
    }
  }
}

function computeCropAttrsForImage(imageWidth: number, imageHeight: number, templateSizes: { width: number; height: number }, key: string): ICropAttrsBatch {
  const targetRatio = templateSizes.width / templateSizes.height;
  const imageRatio = imageWidth / imageHeight;

  let cropWidth: number;
  let cropHeight: number;

  if (imageRatio > targetRatio) {
    cropHeight = imageHeight;
    cropWidth = imageHeight * targetRatio;
  } else {
    cropWidth = imageWidth;
    cropHeight = imageWidth / targetRatio;
  }

  const x = (imageWidth - cropWidth) / 2;
  const y = (imageHeight - cropHeight) / 2;

  return {
    x,
    y,
    width: cropWidth,
    height: cropHeight,
    initWidth: imageWidth,
    initHeight: imageHeight,
    key,
  };
}

export async function deserializeStepParams(step: { action: BATCH_ACTIONS; params: Record<string, unknown> }, selectedData?: IBatchData[]): Promise<{ attrs: any; skip?: string }> {
  switch (step.action) {
    case BATCH_ACTIONS.removeBackground:
    case BATCH_ACTIONS.aiEnhance:
      return { attrs: {} };

    case BATCH_ACTIONS.wfx: {
      const { effect, editorLibCategoryId, params, overlayResourceUrl, overlayBlendMode, overlayIntensity } = step.params;
      if (!effect && !overlayResourceUrl) {
        return { attrs: null, skip: 'no_effect_selected' };
      }
      return {
        attrs: {
          effect: effect ?? null,
          editorLibCategoryId: editorLibCategoryId ?? null,
          params: params ?? {},
          overlayResourceUrl: overlayResourceUrl ?? null,
          overlayBlendMode: overlayBlendMode ?? 'normal',
          overlayIntensity: overlayIntensity ?? 100,
        },
      };
    }

    case BATCH_ACTIONS.adjust: {
      const { params } = step.params;
      if (!params || Object.keys(params as Record<string, number>).length === 0) {
        return { attrs: null, skip: 'no_adjust_params' };
      }
      return {
        attrs: { params },
      };
    }

    case BATCH_ACTIONS.applyBackground: {
      const { textureUrl, color, objectPosition, ratio } = step.params;
      let texture: HTMLImageElement | undefined;
      if (textureUrl) {
        try {
          texture = await loadingImage(textureUrl as string);
        } catch {
          return { attrs: null, skip: 'texture_unavailable' };
        }
      }
      return {
        attrs: { texture, color, objectPosition, ratio },
      };
    }

    case BATCH_ACTIONS.resize:
      return {
        attrs: {
          width: step.params.width,
          height: step.params.height,
          usePadding: step.params.usePadding,
          bgColor: step.params.bgColor,
        },
      };

    case BATCH_ACTIONS.applyCrop: {
      const { templateSizes, rotate, flip } = step.params as {
        templateSizes: { width: number; height: number };
        rotate: number;
        flip: { x: number; y: number };
      };

      if (!selectedData?.length) {
        return { attrs: null, skip: 'no_selected_data' };
      }

      const cropAttrs: ICropAttrsBatch[] = await Promise.all(
        selectedData.map(async item => {
          const img = await loadingImage(item.result.src);
          return computeCropAttrsForImage(img.width, img.height, templateSizes, item.key);
        }),
      );

      return {
        attrs: { attrs: cropAttrs, rotate, flip },
      };
    }

    case BATCH_ACTIONS.watermark: {
      const { logoUrl, position, opacity, size, rotateAngle } = step.params as {
        logoUrl: string | null;
        position: IWatermarkAttrs['position'];
        opacity: number;
        size: number;
        rotateAngle: number;
      };

      if (!logoUrl) {
        return { attrs: null, skip: 'logo_unavailable' };
      }

      let logo: HTMLImageElement;
      try {
        logo = await loadingImage(logoUrl);
      } catch {
        return { attrs: null, skip: 'logo_unavailable' };
      }

      watermarkGenerator.init(logo);
      watermarkGenerator.attrs = { position, opacity, size, rotateAngle };

      return { attrs: {} };
    }

    case BATCH_ACTIONS.generateAIBackground: {
      const { prompt } = step.params;
      if (!prompt) {
        return { attrs: null, skip: 'prompt_unavailable' };
      }
      return { attrs: { prompt } };
    }

    // Non-replayable: depends on session-specific enhanceMapping URLs that expire
    case BATCH_ACTIONS.applyEnhance:
      return { attrs: null, skip: 'non_replayable_action' };

    case BATCH_ACTIONS.applyWfx:
      return { attrs: null, skip: 'non_replayable_action' };

    case BATCH_ACTIONS.applyAdjust:
      return { attrs: null, skip: 'non_replayable_action' };

    case BATCH_ACTIONS.composedEnhance: {
      const { enhanceEffectId, enhanceIntensity, blurEffectId, blurIntensity } = step.params as {
        enhanceEffectId: string | null;
        enhanceIntensity: number;
        blurEffectId: string | null;
        blurIntensity: number;
      };
      if (!enhanceEffectId && !blurEffectId) {
        return { attrs: null, skip: 'no_effect_selected' };
      }
      return {
        attrs: {
          enhanceEffectId: enhanceEffectId ?? null,
          enhanceIntensity: enhanceIntensity ?? 0,
          blurEffectId: blurEffectId ?? null,
          blurIntensity: blurIntensity ?? 0,
          statusIndicatorMode: 'both',
        },
      };
    }

    default: {
      const exhaustiveCheck: never = step.action;
      return exhaustiveCheck;
    }
  }
}
